<template>
	<div class="ModelPredictions">
		<div v-for="(row, index) in rows" :key="index" class="ModelPredictions-row">
			<template v-for="(prediction, i) in predictions">
				<span
					v-if="prediction.categoryId === row.categoryId"
					:key="i"
					class="ModelPredictions-bar"
					:style="{
						left: computeDisplay(prediction, 'left'),
						right: computeDisplay(prediction, 'right'),
						backgroundColor: row.color,
						color: textColor(row.color)
					}"
					v-clo-tooltip
					:title="prediction.label"
					role="button"
					@click="$emit('prediction-click', { prediction, $event })"
				>
					{{ i + 1 }}: {{ prediction.label }}
				</span>
			</template>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		predictions: {
			type: Array,
			default() {
				return [];
			}
		},
		width: {
			type: Number,
			required: true
		},
		offset: {
			type: Number,
			default: 0,
		},
		zoom: {
			type: Number,
			default: 1,
		},
		basePxPerSec: {
			type: Number,
			default: 250,
		}
	},
	computed: {
		rows() {
			const rows = [];
			this.predictions.forEach(x => {
				if (!rows.find(row => row.categoryId === x.categoryId)) {
					rows.push({
						categoryId: x.categoryId,
						color: x.color
					});
				}
			});
			return rows;
		}
	},
	methods: {
		textColor(backgroundColor) {
			return this.contrast(this.hexToRgb(backgroundColor), [255, 255, 255]) >= 4.5 ? '#fff' : '#000';
		},
		hexToRgb(hex) {
			const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
			return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
		},
		luminance(r, g, b) {
			const a = [r, g, b].map(function(v) {
				v /= 255;
				return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
			});
			return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
		},
		contrast(rgb1, rgb2) {
			const lum1 = this.luminance(rgb1[0], rgb1[1], rgb1[2]);
			const lum2 = this.luminance(rgb2[0], rgb2[1], rgb2[2]);
			const brightest = Math.max(lum1, lum2);
			const darkest = Math.min(lum1, lum2);
			return (brightest + 0.05) / (darkest + 0.05);
		},
		convertSecondsToProportion(t, offset, zoom) {
			const pxFromStart = t * (this.basePxPerSec * zoom);
			const pxCursor = offset * this.width;
			return ((pxFromStart - pxCursor)/this.width) + 0.5;
		},
		computeDisplay(prediction, side) {
			switch (side) {
				case 'left':
					return (this.convertSecondsToProportion(prediction.startTime, this.offset, this.zoom)) * 100 + '%';
				case 'right':
					return (1 - this.convertSecondsToProportion(prediction.endTime, this.offset, this.zoom)) * 100 + '%';
			}
		},
	}
};
</script>

<style lang="scss" scoped>
.ModelPredictions {
	background-color: $color-neutral-0;
	@include text(1);
	color: $color-white;
	&-row {
		position: relative;
		height: 14px;
	}
	&-bar {
		position: absolute;
		height: 14px;
		white-space: nowrap;
		padding: 0 $space-xs;
	}
}
</style>
